// src/CBCAppWrapper.jsx
import React, { Suspense, useEffect, useMemo, useState } from "react";
import { lazyWithRetry } from "./lib/lazyWithRetry";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import CBCLogin from "./CBCLogin";
import { apiFetch, clearAuth } from "./apiFetch";

// ✅ add these imports (required for the dialog UI)
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { Button } from "./components/ui/button";

// The ordering app is code-split so the login screen stays small. When a
// cached user exists we start fetching it during boot, alongside /api/me.
const loadOrderingLayout = () => import("./CBCOrderingLayout");
const CBCOrderingLayout = lazyWithRetry(loadOrderingLayout);

// Acting-customer account in effect for a request (apiFetch sends it as a
// header, and /api/me's account data depends on it).
function readActingAccountId() {
  try {
    const raw = localStorage.getItem("cbc_acting_customer");
    const a = raw ? JSON.parse(raw) : null;
    return a?.accountId && a?.email ? String(a.accountId) : null;
  } catch {
    return null;
  }
}

// Best-effort CSRF warmup (never decides auth) in parallel with the GET
// /api/me session check — GETs don't need the CSRF cookie (apiFetch only
// attaches X-CSRF-Token on write methods). Returns { res, actingAccountId }.
async function fetchMeWithCsrfWarmup() {
  const actingAccountId = readActingAccountId();
  const csrf = apiFetch("/api/csrf", { method: "GET" }).catch(() => {});
  const mePromise = apiFetch("/api/me", { method: "GET" });
  const [res] = await Promise.all([mePromise, csrf]);
  return { res, actingAccountId };
}

// ✅ UI-only mode (frontend .env): REACT_APP_UI_ONLY=true
const UI_ONLY = String(process.env.REACT_APP_UI_ONLY || "").toLowerCase() === "true";

// ✅ Default dev user for UI-only mode
const DEV_USER = {
  email: "admin@test.com",
  name: "Dev Superuser",
  role: "admin", // keep admin as primary
  roles: ["admin", "production", "cs"], // ✅ if UI checks roles array
  isAdmin: true, // ✅ if UI checks isAdmin
  permissions: ["*"], // ✅ if UI checks permissions
};

export default function CBCAppWrapper({ initialProduct }) {
  const [booting, setBooting] = useState(true);

  // Last /api/me payload (+ the acting account it was fetched under), handed to
  // CBCOrderingLayout so it can skip its own initial /api/me round-trip.
  const [mePayload, setMePayload] = useState(null);

  // ✅ NEW: session expired modal state
  const [sessionExpired, setSessionExpired] = useState(false);

  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("cbc_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // Listen for global session-expired event (fired by apiFetch on 401).
  // Suppress if the user was never logged in (e.g. landing on set-password link).
  useEffect(() => {
    function onExpired() {
      const hasSession = !!localStorage.getItem("cbc_user");
      if (hasSession) setSessionExpired(true);
    }
    window.addEventListener("cbc:session-expired", onExpired);
    return () => window.removeEventListener("cbc:session-expired", onExpired);
  }, []);

  // ✅ Read login-mode + token from URL (no router required)
  const loginParams = useMemo(() => {
    try {
      const url = new URL(window.location.href);
      const mode = String(url.searchParams.get("mode") || "").trim();
      const email = String(url.searchParams.get("email") || "").trim();
      const token = String(url.searchParams.get("token") || "").trim();

      // we only care about the special set-password link
      const initialMode = mode === "set-password" ? "set-password" : "login";

      return { initialMode, email, token };
    } catch {
      return { initialMode: "login", email: "", token: "" };
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      try {
        // ✅ UI-only: do NOT hit API; just ensure a local user exists
        if (UI_ONLY) {
          const cached = (() => {
            try {
              const raw = localStorage.getItem("cbc_user");
              return raw ? JSON.parse(raw) : null;
            } catch {
              return null;
            }
          })();

          const u = cached?.email ? cached : DEV_USER;

          if (!cancelled) {
            setUser(u);
            localStorage.setItem("cbc_user", JSON.stringify(u));
          }
          return;
        }

        // Start downloading the ordering app now if we expect to land in it.
        if (user?.email) loadOrderingLayout().catch(() => {});

        // Cookie-based session check (CSRF warmup runs in parallel)
        const { res: me, actingAccountId } = await fetchMeWithCsrfWarmup();

        if (me?.ok && me?.user?.email) {
          if (!cancelled) {
            setMePayload({ res: me, actingAccountId });
            setUser(me.user);
            localStorage.setItem("cbc_user", JSON.stringify(me.user));
          }
        } else {
          // Only clear if server explicitly says "not logged in"
          if (!cancelled) {
            setUser(null);
            localStorage.removeItem("cbc_user");
          }
        }
      } catch (err) {
        /**
         * IMPORTANT:
         * Do NOT immediately clear cached user on any error.
         * This is the "Save Quote logs me out" pattern:
         * - transient 401 / network / CORS -> wrapper wipes user -> UI jumps to login
         *
         * Instead:
         * - keep cached user if present
         * - only show login if we truly have no cached user
         */
        const hasCachedUser = !!user?.email;
        if (!hasCachedUser && !cancelled) {
          setUser(null);
          localStorage.removeItem("cbc_user");
        }
      } finally {
        if (!cancelled) setBooting(false);
      }
    }

    boot();
    return () => {
      cancelled = true;
    };
    // Intentionally NOT depending on `user` to avoid boot loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function onStorage(e) {
      if (e.key !== "cbc_user") return;

      try {
        const raw = localStorage.getItem("cbc_user");
        setUser(raw ? JSON.parse(raw) : null);
      } catch {
        setUser(null);
      }
    }

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  async function handleLogin(_loginResponse) {
    // ✅ UI-only: accept login locally (no API)
    if (UI_ONLY) {
      const u = DEV_USER;
      setUser(u);
      localStorage.setItem("cbc_user", JSON.stringify(u));

      // keep URL cleanup behavior consistent
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("mode");
        url.searchParams.delete("email");
        url.searchParams.delete("token");
        window.history.replaceState({}, "", url.pathname + url.search);
      } catch {}
      return;
    }

    try {
      // Fetch the ordering app chunk while /api/me is in flight.
      loadOrderingLayout().catch(() => {});

      // Session check (CSRF warmup runs in parallel)
      const { res: me, actingAccountId } = await fetchMeWithCsrfWarmup();

      if (me?.ok && me?.user?.email) {
        setMePayload({ res: me, actingAccountId });
        setUser(me.user);
        localStorage.setItem("cbc_user", JSON.stringify(me.user));

        // ✅ After login, clean the URL so token doesn't stay in address bar
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("mode");
          url.searchParams.delete("email");
          url.searchParams.delete("token");
          window.history.replaceState({}, "", url.pathname + url.search);
        } catch {}
      } else {
        // Only clear if login truly failed
        localStorage.removeItem("cbc_user");
        setUser(null);
      }
    } catch {
      // If login flow had a transient issue, don't forcibly log out a cached user.
      // But if there's no cached user, remain logged out.
      const hasCachedUser = !!localStorage.getItem("cbc_user");
      if (!hasCachedUser) {
        localStorage.removeItem("cbc_user");
        setUser(null);
      }
    }
  }

  async function handleLogout() {
    // ✅ UI-only: local logout only
    if (!UI_ONLY) {
      try {
        await apiFetch("/api/logout", { method: "POST" });
      } catch {}
    }
    clearAuth();
    localStorage.removeItem("cbc_user");
    setUser(null);
  }

  if (booting) {
    return (
      <div className="min-h-screen grid place-items-center text-gray-600">
        Booting…
      </div>
    );
  }

  // Safer than !!user (prevents weird truthy objects)
  const isAuthed = !!user?.email;

  return (
    <>
      {isAuthed ? (
        <ChunkErrorBoundary>
        <Suspense
          fallback={
            <div className="min-h-screen grid place-items-center text-gray-600">
              Loading…
            </div>
          }
        >
          <CBCOrderingLayout
            user={user}
            onLogout={handleLogout}
            initialProduct={initialProduct}
            initialMe={
              mePayload && mePayload.res?.user?.email === user?.email ? mePayload : null
            }
          />
        </Suspense>
        </ChunkErrorBoundary>
      ) : (
        <CBCLogin
          onLogin={handleLogin}
          initialMode={loginParams.initialMode}
          initialEmail={loginParams.email}
          initialToken={loginParams.token}
        />
      )}

      {/* ✅ NEW: Session expired popup */}
      <Dialog open={sessionExpired}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Session Expired</DialogTitle>
          </DialogHeader>

          <p className="text-sm text-slate-600">
            Your session has expired for security reasons. Please log in again to continue.
          </p>

          <DialogFooter>
            <Button
              onClick={() => {
                setSessionExpired(false);
                setUser(null); // forces login screen
                localStorage.removeItem("cbc_user");
                clearAuth();
              }}
            >
              Log in again
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
